/**
 * Mycelium Query Bridge
 *
 * Subscribes to Mycelium row-change events and maps them to React Query
 * cache invalidation. Replaces scattered manual `invalidateQueries` calls
 * in mutation hooks with a single durable event-driven pipeline.
 *
 * Row-change events flow: Postgres trigger → PGMQ → mycelium-worker →
 * EventBackbone → this bridge → queryClient.invalidateQueries()
 */

import { useEffect, useRef } from 'react';
import { myceliumEventBackbone } from '@flowstate/core';
import type { MyceliumEvent } from '@flowstate/core';
import { queryClient, queryKeys } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

// ── Row-change payload shape (mirrors RowChangePayload from mycelium-core) ──

interface RowChangeFields {
  op?: string;
  table?: string;
  id?: string;
  organizationId?: string | null;
}

function readRowChangeFields(payload: unknown): RowChangeFields {
  if (!payload || typeof payload !== 'object') { return {}; }
  const p = payload as Record<string, unknown>;
  return {
    op: typeof p.op === 'string' ? p.op : undefined,
    table: typeof p.table === 'string' ? p.table : undefined,
    id: typeof p.id === 'string' ? p.id : undefined,
    organizationId:
      typeof p.organizationId === 'string'
        ? p.organizationId
        : p.organizationId === null
          ? null
          : undefined,
  };
}

/** Strip schema prefix: 'public.todos' → 'todos' */
function normalizeTable(table: string): string {
  return table.replace(/^(public\.|auth\.|storage\.)/, '');
}

// ── Table → query key invalidation mapping ──────────────────────────────

const TABLE_INVALIDATION_MAP: Record<string, (readonly string[] | string)[]> = {
  todos: [
    queryKeys.todos.all,
    ['todo-statistics'],
    ['todo-stats'],
    ['systemState'],
    ['quickActions'],
  ],
  tasks: [
    queryKeys.tasks.all,
    ['systemState'],
    ['quickActions'],
  ],
  notifications: [
    queryKeys.notifications.all,
  ],
  clients: [
    queryKeys.clients.all,
  ],
  projects: [
    queryKeys.projects.all,
  ],
  invoices: [
    queryKeys.accounting.all,
  ],
  // cortex_messages intentionally skipped — AI chat uses streaming, not query cache
};

function invalidateTable(table: string): void {
  const keys = TABLE_INVALIDATION_MAP[table];
  if (!keys) { return; }

  for (const key of keys) {
    queryClient.invalidateQueries({ queryKey: key as unknown as readonly unknown[] });
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────

export function useMyceliumQueryBridge() {
  const { user } = useAuth();
  const userId = user?.id;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  useEffect(() => {
    const subscriberName = 'QueryInvalidationBridge';

    myceliumEventBackbone.subscribe({
      name: subscriberName,
      domains: ['task', 'notification', 'crm', 'accounting'],
      handler: (event: MyceliumEvent) => {
        try {
          // Handle bulk row-change events
          if (event.type === 'bulk_row_change') {
            const payload = event.payload as Record<string, unknown> | undefined;
            const table = typeof payload?.table === 'string' ? normalizeTable(payload.table) : undefined;
            if (table) {
              invalidateTable(table);
            } else {
              // Invalidate all known domains
              for (const t of Object.keys(TABLE_INVALIDATION_MAP)) {
                invalidateTable(t);
              }
            }
            return;
          }

          // Handle per-row change events
          const fields = readRowChangeFields(event.payload);
          const table = fields.table ? normalizeTable(fields.table) : undefined;
          if (!table) { return; }

          invalidateTable(table);
        } catch (error) {
          logger.error('QueryInvalidationBridge handler error', {
            eventId: event.id,
            eventType: event.type,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      },
      priority: 1, // Run after business logic, before audit
    });

    logger.info('QueryInvalidationBridge subscribed to Mycelium');

    return () => {
      myceliumEventBackbone.unsubscribe(subscriberName);
      logger.info('QueryInvalidationBridge unsubscribed from Mycelium');
    };
  }, []); // Subscribe once — userIdRef stays current
}
